import { useNavigate } from "react-router-dom";
import { LogOut, MapPin, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Outlet } from "react-router-dom";

const OFFICE_ADDRESS =
  "Edifício Liberty Mall Shopping — Asa Norte, SCN Q. 2, Bloco D, Torre A, Sala 1101 — Brasília/DF — CEP 70077-900";
const OFFICE_PHONE = "(61) 9 9889-7815";

export const PortalShell = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const initials = user.name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-paper">
      {/* Top contact bar */}
      <div className="bg-ink/95 text-slate-300 text-[11px] border-b border-white/10">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
          <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3 text-gold" />
            <span>{OFFICE_ADDRESS}</span>
          </div>
          <a href={`tel:${OFFICE_PHONE.replace(/\D/g, "")}`} className="flex items-center gap-2 hover:text-white">
            <Phone className="w-3 h-3 text-gold" />
            <span>{OFFICE_PHONE}</span>
          </a>
        </div>
      </div>

      <header className="bg-ink text-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo height={48} bare />
            <div className="hidden sm:block border-l border-white/20 pl-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-300">
                Portal Institucional
              </div>
              <div className="serif text-lg text-white">{user.name}</div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="gap-2 text-white hover:text-white hover:bg-white/10"
                data-testid="portal-user-menu"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-gold text-ink text-xs font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">{user.name}</span>
                  <span className="text-xs text-slate-500">{user.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="portal-logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 mt-12 py-8">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-xs text-slate-500">
          <div className="flex flex-wrap justify-between gap-4 mb-2">
            <div className="font-semibold">CEAP Brasil &amp; Lopes</div>
            <div>Consultoria para Municípios Brasileiros</div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-400">
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3" />
              {OFFICE_ADDRESS}
            </div>
            <div className="flex items-center gap-1.5">
              <Phone className="w-3 h-3" />
              {OFFICE_PHONE}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
